/**
 * "Cerrar sesión" desde el Perfil (006 · T049, research R-15, FR-020).
 *
 * 1. `POST /session/logout` (002, sin cambios): la API revoca la sesión (`LOGOUT`) y limpia la
 *    cookie. El dispositivo sigue `ACTIVE` en el servidor hasta que el administrador apruebe una
 *    re-vinculación.
 * 2. **Pase lo que pase** (204, sin red, `423` con la empresa suspendida, `401` con la sesión ya
 *    vencida): se borra la clave del dispositivo (`resetKey()`), se vacía la caché en memoria, se
 *    deja `DEVICE_KEY_QUERY_KEY` en `false`, se olvida cualquier solicitud de re-vinculación vieja y
 *    se navega a `/dispositivo-no-registrado` **sin** `reason` (no es una sesión vencida).
 *
 * Borrar la clave es lo que hace aprobable la vinculación posterior: la aprobación responde
 * `409 CONFLICT` si la clave del solicitante ya está registrada (002 wiring §4.3), así que la
 * solicitud que envíe el teléfono debe llevar una clave nueva.
 *
 * El logout va con `notifyErrors: false`: un `423`/`401` no emite el evento global, así que el
 * puente de `__root.tsx` no navega antes a `/servicio-suspendido` o a la variante "sesión vencida"
 * de no registrado — sin salto intermedio, la única navegación es la de aquí.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { resetKey } from '../../device/keys';
import { logoutStaffSession } from '../access/api';
import { DEVICE_KEY_QUERY_KEY } from '../access/device-key-query';
import { clearRebindRequest } from '../access/rebind-store';

export interface UseLogoutResult {
  logout: () => Promise<void>;
  pending: boolean;
}

export function useLogout(): UseLogoutResult {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const logout = useCallback(async () => {
    setPending(true);
    try {
      await logoutStaffSession({ notifyErrors: false });
    } catch {
      // Sin red, `423` o `401`: la limpieza local va igual (FR-020). No se muestra el error.
    }
    try {
      await resetKey();
    } catch {
      // Un almacenamiento que no deja borrar no debe dejar al trabajador atrapado en el perfil.
    }
    queryClient.clear();
    queryClient.setQueryData(DEVICE_KEY_QUERY_KEY, false);
    clearRebindRequest();
    await navigate({ to: '/dispositivo-no-registrado', replace: true });
  }, [navigate, queryClient]);

  return { logout, pending };
}
